'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import {
  AgentApproval, AgentChatMessage, AgentToolEvent, CollageOp, CollagePageState, PendingAction,
} from '@/lib/types';
import ConfirmCard, { type Decision } from './ConfirmCard';
import { usePrivateMode } from '@/lib/privateMode';
import { useConfirm } from '@/components/ConfirmDialog';
import LlmPicker, { useLlmChoice } from '@/components/LlmPicker';

const HISTORY_KEY = 'collage-assistant-history';

// Approvals the model may chain unattended in one user turn before the card returns.
const AUTO_APPROVE_CHAIN_MAX = 10;
const HISTORY_VERSION = 1;
const MAX_MESSAGES = 40;
const MAX_BYTES = 60_000;

interface ChatEntry {
  role: 'user' | 'assistant';
  content: string;
  tools?: AgentToolEvent[];
  ops?: CollageOp[];
  /** Kept after the decision so the transcript records what was allowed. */
  pending?: PendingAction;
  /** Settled by auto-approve rather than a click. */
  auto?: boolean;
  decided?: 'allow' | 'deny';
  /** Carried for the model's benefit only — raw protocol, never shown to the user. */
  hidden?: boolean;
}

interface StoredHistory {
  version: number;
  messages: ChatEntry[];
  trimmed?: boolean;
}

interface CollageAssistantPanelProps {
  /** Snapshot of the page, so the assistant can talk about what the user is looking at. */
  getPageState: () => CollagePageState;
  onCollageOps: (ops: CollageOp[]) => void;
}

/** Oldest-first drop until both caps are satisfied. */
function trim(messages: ChatEntry[]): { messages: ChatEntry[]; trimmed: boolean } {
  let out = messages.slice(-MAX_MESSAGES);
  let trimmed = out.length < messages.length;
  while (out.length > 1 && JSON.stringify(out).length > MAX_BYTES) {
    out = out.slice(1);
    trimmed = true;
  }
  return { messages: out, trimmed };
}

export default function CollageAssistantPanel({ getPageState, onCollageOps }: CollageAssistantPanelProps) {
  const privateEnabled = usePrivateMode((s) => s.enabled);
  const { confirm } = useConfirm();
  // 'qwen' as the auto target, not dolphin: an uncensored model here must be chosen
  // deliberately, matching what _resolve_collage_llm does server-side.
  const { choice, resolved, change, confirmGpu } = useLlmChoice('collage-assistant-llm', 'qwen');

  const [messages, setMessages] = useState<ChatEntry[]>([]);
  const [wasTrimmed, setWasTrimmed] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  /**
   * "Always" on a confirmation card. Session-only by design: React state, never persisted,
   * so a reload or Clear puts the gate back. Destructive actions are excluded — those are
   * precisely what the gate is for, and they keep asking.
   */
  const [autoApprove, setAutoApprove] = useState(false);
  const autoApproveRef = useRef(false);
  autoApproveRef.current = autoApprove;
  // A model that keeps proposing actions could otherwise chain approvals forever without
  // the user seeing a thing. After this many in one user turn, the card comes back.
  const autoChain = useRef(0);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (!raw) return;
      const parsed: StoredHistory = JSON.parse(raw);
      // A format change discards rather than crashes — there is no server copy to migrate from
      if (parsed?.version !== HISTORY_VERSION || !Array.isArray(parsed.messages)) return;
      setMessages(parsed.messages);
      setWasTrimmed(Boolean(parsed.trimmed));
    } catch {
      /* start fresh */
    }
  }, []);

  const persist = useCallback((next: ChatEntry[], trimmedFlag: boolean) => {
    try {
      localStorage.setItem(
        HISTORY_KEY,
        JSON.stringify({ version: HISTORY_VERSION, messages: next, trimmed: trimmedFlag } satisfies StoredHistory)
      );
    } catch {
      /* non-persistent is fine */
    }
  }, []);

  // A transcript can name private lists, so it must not outlive private mode
  const prevPrivate = useRef(privateEnabled);
  useEffect(() => {
    if (prevPrivate.current && !privateEnabled) {
      setMessages([]);
      setWasTrimmed(false);
      try {
        localStorage.removeItem(HISTORY_KEY);
      } catch {
        /* ignore */
      }
    }
    prevPrivate.current = privateEnabled;
  }, [privateEnabled]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, error]);

  const send = async (history: ChatEntry[], approve?: AgentApproval) => {
    setSending(true);
    setError(null);
    try {
      const payload: AgentChatMessage[] = history.map((m) => ({ role: m.role, content: m.content }));
      const res = await api.collageChat({
        messages: payload,
        page_state: getPageState(),
        approve,
        // Sent only in private mode; the backend clamps to qwen regardless
        llm: privateEnabled ? resolved : undefined,
      });

      const pa = res.pending_action;
      const auto = Boolean(pa) && autoApproveRef.current && !pa!.destructive
        && autoChain.current < AUTO_APPROVE_CHAIN_MAX;
      setPending(auto ? null : res.pending_action);
      const next: ChatEntry[] = [...history, {
        role: 'assistant' as const,
        content: res.message,
        tools: res.tool_events,
        ops: res.collage_ops,
        pending: pa ?? undefined,
        ...(auto ? { decided: 'allow' as const, auto: true } : {}),
      }];
      const { messages: kept, trimmed } = trim(next);
      setMessages(kept);
      setWasTrimmed((w) => w || trimmed);
      persist(kept, wasTrimmed || trimmed);

      if (res.collage_ops.length > 0) onCollageOps(res.collage_ops);

      if (auto && pa) {
        autoChain.current += 1;
        const resumed: ChatEntry[] = [
          ...kept,
          { role: 'assistant' as const, content: pa.assistant_content, hidden: true },
        ];
        setMessages(resumed);
        await send(resumed, { id: pa.id, tool: pa.tool, args: pa.args, decision: 'allow' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  /**
   * Resume the paused turn. The model's own tool call is appended verbatim — the server
   * hands back the exact text so the transcript cannot drift from what it asked for.
   */
  const decide = async (decision: Decision) => {
    if (!pending || sending) return;
    if (decision === 'allow-all') {
      setAutoApprove(true);
      autoApproveRef.current = true;   // the in-flight resume must already see it
      autoChain.current = 0;
    }
    const settled: 'allow' | 'deny' = decision === 'deny' ? 'deny' : 'allow';
    const resumed: ChatEntry[] = [
      ...messages.map((m) => (m.pending?.id === pending.id ? { ...m, decided: settled } : m)),
      { role: 'assistant' as const, content: pending.assistant_content, hidden: true },
    ];
    setMessages(resumed);
    setPending(null);
    send(resumed, { id: pending.id, tool: pending.tool, args: pending.args, decision: settled });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || pending) return;
    if (privateEnabled && !(await confirmGpu(resolved))) return;
    autoChain.current = 0;
    const history = [...messages, { role: 'user' as const, content: text }];
    setMessages(history);
    setInput('');
    send(history);
  };

  const handleClear = async () => {
    if (messages.length === 0) return;
    if (!(await confirm({
      title: 'Clear conversation',
      message: 'Delete this conversation? It is only stored in this browser.',
      confirmText: 'Clear',
      variant: 'warning',
    }))) return;
    // After the confirm, not before: backing out of the dialog must not quietly disarm
    // auto-approve, which the user would then not know had happened.
    setAutoApprove(false);
    autoChain.current = 0;
    setMessages([]);
    setWasTrimmed(false);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--border-subtle)] text-xs shrink-0">
        {privateEnabled ? (
          <LlmPicker
            choice={choice}
            resolved={resolved}
            onChange={change}
            title="Assistant model — only selectable in private mode"
          />
        ) : (
          <span className="text-[var(--text-muted)]" title="Public mode always uses the local Qwen model">
            Qwen
          </span>
        )}
        <button
          onClick={handleClear}
          disabled={messages.length === 0}
          className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Clear
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {wasTrimmed && messages.length > 0 && (
          <p className="text-[10px] text-center text-[var(--text-muted)]">— earlier messages trimmed —</p>
        )}

        {messages.length === 0 && !sending && (
          <div className="h-full flex items-center justify-center text-center px-3">
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-secondary)]">Ask about your lists, or for ideas.</p>
              <p className="text-xs text-[var(--text-muted)]">
                &ldquo;what lists do I have?&rdquo;<br />
                &ldquo;give me 20 cyberpunk job titles&rdquo;
              </p>
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          m.hidden ? null : (
          <div key={i} data-msg={m.role} className={m.role === 'user' ? 'text-right' : ''}>
            <div
              className={`inline-block max-w-[92%] text-left px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-[var(--accent-soft)] text-[var(--text-primary)]'
                  : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
              }`}
            >
              {m.content}
            </div>
            {/* Page ops are applied by the client, so they never appear in tool_events —
                echo them, or the assistant silently edits the page with no record of it. */}
            {m.ops && m.ops.length > 0 && (
              <div className="mt-1 text-[10px] text-[var(--text-muted)]" data-applied-ops>
                ⚙ {m.ops.map((o) => {
                  if (o.op === 'set_prompt') return 'set the prompt';
                  if (o.op === 'activate_lists') return `activated ${o.names.join(', ')}`;
                  if (o.op === 'select_item') {
                    const pl = o.payload as { list?: string; value?: string; direction?: string };
                    return `changed ${pl.list} → ${pl.value ?? pl.direction ?? 'another item'}`;
                  }
                  if (o.op === 'set_preview') {
                    return (o.payload as { visible?: boolean }).visible ? 'opened the preview' : 'closed the preview';
                  }
                  if (o.op === 'set_settings') {
                    return Object.entries(o.payload)
                      .map(([k, v]) => `${k}=${v === null ? 'random' : v}`).join(', ');
                  }
                  if (o.op === 'prefill_batch') return 'filled the generate form';
                  return o.op;
                }).join(' · ')}
              </div>
            )}
            {/* Tools and page ops ran while the turn was still going; the card is where it
                stopped to ask. Rendering the card first made read-only calls look like they
                had run *after* approval. */}
            {m.tools && m.tools.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {m.tools.map((t, k) => (
                  <span
                    key={k}
                    data-tool-chip={t.tool}
                    title={t.result}
                    className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-elevated)] text-[var(--text-muted)]"
                  >
                    {t.tool}
                  </span>
                ))}
              </div>
            )}
            {m.pending && (
              <div className="mt-2">
                <ConfirmCard action={m.pending} busy={sending} decided={m.decided}
                  auto={m.auto} onDecide={decide} />
              </div>
            )}
          </div>
          )
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <span className="w-3 h-3 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
            thinking…
          </div>
        )}

        {error && (
          <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs space-y-1">
            <p className="break-words">{error}</p>
            <button
              onClick={() => send(messages)}
              className="underline hover:no-underline cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      <div className="p-2 border-t border-[var(--border-subtle)] shrink-0">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={pending ? 'Waiting for your decision…' : 'Ask or instruct…'}
            rows={2}
            disabled={sending || Boolean(pending)}
            className="flex-1 resize-none px-2 py-1.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg text-sm focus:border-[var(--accent)] focus:outline-none disabled:opacity-60"
          />
          <button
            onClick={handleSend}
            disabled={sending || Boolean(pending) || !input.trim()}
            className="btn btn-primary px-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>
    </>
  );
}
